// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/checkInClaimStorage.ts
// 🔧 PURPOSE: Le richieste di ACCESSO AUTOMATICO dopo il check-in online,
//             tenute sul telefono (localStorage `nva_checkin_claims`).
//
//             Come funziona (channel manager, api/public/checkin/claim):
//             aprendo il check-in l'app crea un segreto (32 byte casuali,
//             base64url senza "="), lo tiene qui e manda al channel manager
//             solo la sua impronta (SHA-256 esadecimale del testo base64url)
//             nell'indirizzo dell'iframe. Il check-in inviato la salva; il
//             segreto non lascia mai il telefono se non nella richiesta che
//             lo "riscatta". Il channel manager risponde:
//             - pending: il check-in aspetta lo smistamento dell'host;
//             - ready: token e prenotazione, l'ospite entra da solo (una
//               volta sola: poi il segreto non vale più);
//             - 404: segreto sconosciuto, scaduto, già usato, check-in
//               cancellato (sempre la stessa risposta).
//
//             Qui solo lo storage: il watcher (components/CheckInClaimWatcher)
//             decide quando chiedere, CheckIn crea e marca le voci, la Home
//             e il login leggono "in attesa". Regole (critica 13-17):
//             - al massimo 3 voci; aprendo di nuovo il check-in si riusa
//               l'ultima mai arrivata alla validazione, invece di crearne
//               un'altra che spingerebbe fuori quella inviata davvero;
//             - si chiede al server solo per le voci arrivate a
//               CHECKIN_VALIDATED (il messaggio arriva anche senza embedded)
//               o già "pending";
//             - dal check-in aperto con nvaApp=2 arrivano anche
//               CHECKIN_FORM_SUBMITTED e CHECKIN_COMPLETED: la voce si segna
//               "inviata" (submittedAt) e si chiede SUBITO (requestClaimCheck
//               urgente), senza aspettare il giro dei 30 secondi;
//             - "in attesa" (Home, login, pagina del check-in) solo dopo che
//               il server ha risposto pending, mai per un check-in aperto e
//               mai inviato;
//             - ogni accesso riuscito (store della sessione) e ogni uscita
//               tolgono tutto: chi esce non rientra da solo.
//             Ogni accesso allo storage è protetto: in navigazione privata o
//             con i dati bloccati localStorage lancia, e il check-in non deve
//             morire per questo (senza storage l'accesso automatico non c'è).
// ============================================

/** Le richieste (la stessa chiave che useGuestSession toglie all'accesso e all'uscita). */
export const CHECKIN_CLAIMS_KEY = 'nva_checkin_claims';
/** Il ricordo dell'ultima richiesta sparita mentre era in attesa: il login la precompila. */
export const CHECKIN_CLAIM_EXPIRED_KEY = 'nva_checkin_claim_expired';
/** Annunciato a ogni scrittura: la Home (guestClaimState) e il watcher si aggiornano. */
export const CHECKIN_CLAIMS_EVENT = 'nva_checkin_claims_changed';
/** "Controlla adesso": dalle push checkin_linked e dalla pagina del check-in. */
export const CHECKIN_CLAIM_CHECK_EVENT = 'nva_checkin_claim_check';

export const MAX_CLAIMS = 3;
/** Oltre questo la richiesta è comunque scaduta sul server (appClaimExpiresAt ≤ 30 giorni). */
export const CLAIM_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Validata ma mai vista dal server dopo un giorno: il check-in non è stato inviato. */
export const UNSUBMITTED_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** Quanto resta il ricordo per il login precompilato. */
const EXPIRED_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type CheckInClaimMode = 'normal' | 'unassigned_checkin';

export interface CheckInClaim {
  /** Il segreto (base64url, 43 caratteri): resta qui. */
  nonce: string;
  /** La sua impronta, per riaprire il check-in con la stessa voce. */
  hash: string;
  createdAt: number;
  /** CHECKIN_VALIDATED ricevuto: da qui il check-in può essere stato inviato. */
  validatedAt?: number;
  mode?: CheckInClaimMode;
  /** Riferimento ed email scritti nel check-in: per il login di riserva e precompilato. */
  savedBookingRef?: string;
  savedEmail?: string;
  /** Il server ha risposto pending almeno una volta: check-in inviato, da smistare. */
  status?: 'pending';
  pendingAt?: number;
  /** Il login di riserva (riferimento + email) è già stato provato. */
  fallbackTried?: boolean;
  /**
   * Il modulo è stato inviato (CHECKIN_FORM_SUBMITTED o CHECKIN_COMPLETED,
   * solo col flusso embedded nvaApp=2): da qui un "non trovato" non vuol
   * più dire "non ancora inviato", anche con l'iframe ancora aperto.
   */
  submittedAt?: number;
}

export interface ExpiredClaimInfo {
  savedBookingRef?: string;
  savedEmail?: string;
  at: number;
}

// ---- localStorage, sempre protetto ----------------------------------------------

const readRaw = (key: string): string | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeRaw = (key: string, value: string | null): boolean => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const announce = (): void => {
  try {
    window.dispatchEvent(new CustomEvent(CHECKIN_CLAIMS_EVENT));
  } catch {
    // CustomEvent assente: chi ascolta rilegge al prossimo evento.
  }
};

const str = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const num = (value: unknown): number | undefined => {
  const n = typeof value === 'string' ? Date.parse(value) : Number(value);
  return isFinite(n) && n > 0 ? n : undefined;
};

/** Una voce letta dal disco, solo se ha la forma giusta. */
function sanitize(entry: unknown): CheckInClaim | null {
  if (!entry || typeof entry !== 'object') return null;
  const e = entry as Record<string, unknown>;
  const nonce = str(e.nonce);
  const hash = str(e.hash);
  const createdAt = num(e.createdAt);
  if (!nonce || !/^[A-Za-z0-9_-]{32,128}$/.test(nonce) || !hash || !createdAt) return null;
  const claim: CheckInClaim = { nonce, hash, createdAt };
  const validatedAt = num(e.validatedAt);
  if (validatedAt) claim.validatedAt = validatedAt;
  if (e.mode === 'normal' || e.mode === 'unassigned_checkin') claim.mode = e.mode;
  const ref = str(e.savedBookingRef);
  if (ref) claim.savedBookingRef = ref;
  const email = str(e.savedEmail);
  if (email) claim.savedEmail = email;
  if (e.status === 'pending') {
    claim.status = 'pending';
    const pendingAt = num(e.pendingAt);
    if (pendingAt) claim.pendingAt = pendingAt;
  }
  if (e.fallbackTried === true) claim.fallbackTried = true;
  const submittedAt = num(e.submittedAt);
  if (submittedAt) claim.submittedAt = submittedAt;
  return claim;
}

/** Scrive la lista (vuota = toglie la chiave) e lo annuncia. */
function writeClaims(list: CheckInClaim[]): void {
  writeRaw(CHECKIN_CLAIMS_KEY, list.length ? JSON.stringify(list) : null);
  announce();
}

/** Le richieste valide, dalla più vecchia; quelle di oltre 30 giorni se ne vanno. */
export function listClaims(now: number = Date.now()): CheckInClaim[] {
  const raw = readRaw(CHECKIN_CLAIMS_KEY);
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    writeRaw(CHECKIN_CLAIMS_KEY, null);
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const list = parsed
    .map(sanitize)
    .filter((c): c is CheckInClaim => !!c && now - c.createdAt < CLAIM_MAX_AGE_MS);
  if (list.length !== parsed.length) writeClaims(list);
  return list;
}

function updateClaim(nonce: string, change: (claim: CheckInClaim) => CheckInClaim | null): void {
  const list = listClaims();
  const index = list.findIndex((c) => c.nonce === nonce);
  if (index < 0) return;
  const next = change(list[index]);
  if (next) list[index] = next;
  else list.splice(index, 1);
  writeClaims(list);
}

// ---- Segreto e impronta ------------------------------------------------------------

const toBase64Url = (bytes: Uint8Array): string => {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** 32 byte casuali in base64url senza "=" (43 caratteri), come vuole il channel manager. */
export function randomClaimSecret(): string | null {
  try {
    const bytes = new Uint8Array(32);
    window.crypto.getRandomValues(bytes);
    return toBase64Url(bytes);
  } catch {
    return null;
  }
}

/** SHA-256 esadecimale dei byte UTF-8 del segreto (il testo base64url, non i 32 byte). */
export async function claimHashOf(secret: string): Promise<string | null> {
  try {
    const subtle = window.crypto?.subtle;
    if (!subtle) return null;
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(secret));
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

// ---- Le operazioni -------------------------------------------------------------------

/**
 * La voce per il check-in che si sta aprendo: l'ultima mai arrivata alla
 * validazione se c'è (aprire e chiudere non riempie la lista), altrimenti
 * una nuova. Oltre 3 esce la più vecchia che il server non ha mai visto in
 * attesa. null senza crypto (http, browser vecchi) o senza storage: il
 * check-in funziona lo stesso, senza accesso automatico.
 */
export async function prepareClaim(now: number = Date.now()): Promise<{ nonce: string; hash: string } | null> {
  const list = listClaims(now);
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const c = list[i];
    if (!c.validatedAt && c.status !== 'pending') {
      // Si riparte da adesso: l'età conta dall'ultima apertura.
      list[i] = { ...c, createdAt: now };
      writeClaims(list);
      return { nonce: c.nonce, hash: c.hash };
    }
  }

  const nonce = randomClaimSecret();
  if (!nonce) return null;
  const hash = await claimHashOf(nonce);
  if (!hash) return null;

  const next = listClaims(now);
  while (next.length >= MAX_CLAIMS) {
    const drop = next.findIndex((c) => c.status !== 'pending');
    next.splice(drop >= 0 ? drop : 0, 1);
  }
  next.push({ nonce, hash, createdAt: now });
  writeClaims(next);
  // Scrittura non riuscita (storage bloccato): senza voce niente impronta,
  // il channel manager non salverebbe un segreto che nessuno può usare.
  if (!listClaims(now).some((c) => c.nonce === nonce)) return null;
  return { nonce, hash };
}

/** CHECKIN_VALIDATED: il check-in di questa voce può partire. */
export function markClaimValidated(
  nonce: string,
  info: { mode?: unknown; savedBookingRef?: unknown; savedEmail?: unknown } = {},
  now: number = Date.now(),
): void {
  updateClaim(nonce, (c) => ({
    ...c,
    validatedAt: now,
    ...(info.mode === 'normal' || info.mode === 'unassigned_checkin' ? { mode: info.mode } : {}),
    ...(str(info.savedBookingRef) ? { savedBookingRef: str(info.savedBookingRef) } : {}),
    ...(str(info.savedEmail) ? { savedEmail: str(info.savedEmail)!.toLowerCase() } : {}),
  }));
}

/**
 * Il modulo del check-in è stato inviato (CHECKIN_FORM_SUBMITTED o
 * CHECKIN_COMPLETED): la voce si chiede subito e un "non trovato" non si
 * scambia più per "non ancora inviato". Se la validazione era sfuggita, la
 * si segna adesso: una voce inviata va comunque chiesta al server.
 */
export function markClaimSubmitted(nonce: string, now: number = Date.now()): void {
  updateClaim(nonce, (c) => (c.submittedAt
    ? c
    : { ...c, submittedAt: now, ...(c.validatedAt ? {} : { validatedAt: now }) }));
}

/** Il server ha risposto pending: check-in inviato, in attesa dello smistamento. */
export function markClaimPending(nonce: string, now: number = Date.now()): void {
  updateClaim(nonce, (c) => (c.status === 'pending' ? c : { ...c, status: 'pending', pendingAt: now }));
}

export function markClaimFallbackTried(nonce: string): void {
  updateClaim(nonce, (c) => ({ ...c, fallbackTried: true }));
}

export function removeClaim(nonce: string): void {
  updateClaim(nonce, () => null);
}

export function clearClaims(): void {
  writeClaims([]);
}

/** Le voci da chiedere al server: arrivate alla validazione, o già in attesa. */
export function claimsToCheck(now: number = Date.now()): CheckInClaim[] {
  return listClaims(now).filter((c) => !!c.validatedAt || c.status === 'pending');
}

/** La voce in attesa più recente (per il banner del login), o null. */
export function latestPendingClaim(now: number = Date.now()): CheckInClaim | null {
  const pending = listClaims(now).filter((c) => c.status === 'pending');
  return pending.length ? pending[pending.length - 1] : null;
}

/** Vero se almeno un check-in inviato da qui aspetta lo smistamento. */
export function hasPendingClaim(now: number = Date.now()): boolean {
  return !!latestPendingClaim(now);
}

// ---- La voce aperta nell'iframe (in memoria) -------------------------------------------

let activeNonce: string | null = null;

/**
 * La voce del check-in aperto adesso nell'iframe: finché è aperto dopo la
 * validazione il watcher chiede spesso (ogni 30 secondi), così chi finisce
 * il check-in entra presto anche senza il messaggio di fine.
 */
export function setActiveClaim(nonce: string | null): void {
  if (activeNonce === nonce) return;
  activeNonce = nonce;
  announce();
}

export const activeClaim = (): string | null => activeNonce;

/**
 * "Controlla adesso" (push checkin_linked, iframe chiuso). `urgent`: il
 * check-in è appena stato inviato (CHECKIN_FORM_SUBMITTED / COMPLETED), il
 * watcher chiede subito anche se l'ultimo giro è di pochi secondi fa: il
 * server conosce il claim e lo fa passare anche durante un blocco 429.
 */
export function requestClaimCheck(options: { urgent?: boolean } = {}): void {
  try {
    window.dispatchEvent(new CustomEvent(CHECKIN_CLAIM_CHECK_EVENT, { detail: { urgent: options.urgent === true } }));
  } catch {
    // niente: il watcher controlla comunque al prossimo giro
  }
}

// ---- Il ricordo per il login -------------------------------------------------------------

/** Una richiesta in attesa è sparita (scaduta, cancellata): il login la precompila. */
export function rememberExpiredClaim(claim: Pick<CheckInClaim, 'savedBookingRef' | 'savedEmail'>, now: number = Date.now()): void {
  const info: ExpiredClaimInfo = { at: now };
  if (claim.savedBookingRef) info.savedBookingRef = claim.savedBookingRef;
  if (claim.savedEmail) info.savedEmail = claim.savedEmail;
  writeRaw(CHECKIN_CLAIM_EXPIRED_KEY, JSON.stringify(info));
}

export function readExpiredClaim(now: number = Date.now()): ExpiredClaimInfo | null {
  const raw = readRaw(CHECKIN_CLAIM_EXPIRED_KEY);
  if (!raw) return null;
  try {
    const e = JSON.parse(raw) as Record<string, unknown>;
    const at = num(e?.at);
    if (!at || now - at > EXPIRED_MAX_AGE_MS) {
      writeRaw(CHECKIN_CLAIM_EXPIRED_KEY, null);
      return null;
    }
    return { at, savedBookingRef: str(e.savedBookingRef), savedEmail: str(e.savedEmail) };
  } catch {
    writeRaw(CHECKIN_CLAIM_EXPIRED_KEY, null);
    return null;
  }
}

export function clearExpiredClaim(): void {
  writeRaw(CHECKIN_CLAIM_EXPIRED_KEY, null);
}
